import { attach, combine, createEffect, createEvent, createStore, sample } from 'effector';
import { updatePermissions } from '@nx-frontend/shared/api';
import { getManageMembersData } from '@nx-frontend/shared/api/services/manage-members';
import { inviteToPortal } from '@nx-frontend/shared/api/services/manage-members/manage-members';
import { $billingInfo, setBillingInfoEvent } from '@nx-frontend/shared/store/billing-info/billing-info';
import { addUserIdsToAllAllowedAllMenuItemsEvent } from '@nx-frontend/shared/store/feature-menu-tree';
import { $hasCoupons } from '@nx-frontend/shared/store/org-coupons';
import { setOrgInfoEvent } from '@nx-frontend/shared/store/org-info';
import { $invites, setInvitesEvent } from '@nx-frontend/shared/store/org-invites';
import {
  $members,
  $orgMemberUserIds,
  setMembersEvent,
  upsertMembersEvent,
} from '@nx-frontend/shared/store/org-members';
import { $orgRole } from '@nx-frontend/shared/store/org-role';
import {
  $orgUsageData,
  endOrgUsageFetchEvent,
  startOrgUsageFetchEvent,
} from '@nx-frontend/shared/store/org-usage/org-usage';
import { $accessSettings } from '@nx-frontend/shared/store/portal-settings';
import { $usersState, setUsersEvent, upsertUsersEvent } from '@nx-frontend/shared/store/users-store';
import { $workspaceInviteEmails, setWorkspaceInvitesEvent } from '@nx-frontend/shared/store/workspace-invites';
import {
  $workspaceMembersState,
  setWorkspaceMembersEvent,
  upsertWorkspaceMembersEvent,
} from '@nx-frontend/shared/store/workspace-members';
import { $workspaceRole } from '@nx-frontend/shared/store/workspace-role';
import {
  BillingInfo,
  BillingInfoPlan,
  ManageMembersContext,
  ManageMembersInvitePayload,
  ManageMembersOrgLimits,
  ManageMembersType,
  ManageMembersView,
  memberRolesOrder,
  MenuItem,
  OrgInfo,
  OrgInvite,
  OrgMember,
  OrgMemberRole,
  OrgUsage,
  Permissions,
  PermissionsResourceType,
  User,
  WorkspaceInvite,
  WorkspaceMember,
} from '@nx-frontend/shared/types';

export type OrgMemberRoleForInvite = Exclude<OrgMemberRole, OrgMemberRole.OWNER>;
export type DraftInviteCount = {
  [key in OrgMemberRoleForInvite]: number;
};
export const initialDraftInviteCount: DraftInviteCount = {
  [OrgMemberRole.MANAGER]: 0,
  [OrgMemberRole.MEMBER]: 0,
  [OrgMemberRole.CLIENT]: 0,
  [OrgMemberRole.GUEST]: 0,
};

export interface ManageMembersState {
  isFetching: boolean;
  type: ManageMembersType;
  viewMode: ManageMembersView;
  orgMaxMembers: number;
  emailInvitesCount: number;
  menuItem?: MenuItem;
  context: ManageMembersContext;
  orgLimits: ManageMembersOrgLimits;
  draftInviteCount: DraftInviteCount;
  isPaymentInfoActive: boolean;
  forceIsOnlySharedAvailable: boolean;
}

export const initialManageMembersState: ManageMembersState = {
  isFetching: true,
  type: ManageMembersType.PORTAL,
  viewMode: ManageMembersView.INVITE,
  orgMaxMembers: 0,
  context: {
    orgId: '',
    workspaceId: '',
    portalUrl: '',
  },
  emailInvitesCount: 0,
  menuItem: undefined,
  orgLimits: {
    membersPerOrg: 0,
    guests: 0,
  },
  draftInviteCount: initialDraftInviteCount,
  isPaymentInfoActive: false,
  forceIsOnlySharedAvailable: false,
};

export const updateViewModeEvent = createEvent<ManageMembersView>();
export const updateOrgMaxMembersEvent = createEvent<number>();
export const updateMenuItemEvent = createEvent<MenuItem>();
export const updateContextEvent = createEvent<ManageMembersContext>();
export const updateOrgLimitsEvent = createEvent<ManageMembersOrgLimits>();
export const updateTypeEvent = createEvent<ManageMembersType>();
export const resetManageMembersEvent = createEvent();
export const updateDraftInviteCountEvent = createEvent<DraftInviteCount>();
export const endFetchingEvent = createEvent();
export const setIsPaymentInfoActiveEvent = createEvent<boolean>();
export const updateForceIsOnlySharedAvailableEvent = createEvent<boolean>();

export const updatePermissionsFx = createEffect(
  async ({
    permissions,
    targetId,
    invites,
    workspaceId,
    portalUrl,
    orgId,
    fetchOrgMembers,
  }: {
    permissions: Permissions;
    targetId: string;
    invites?: { email: string; role: string; orgRole?: OrgMemberRole }[];
    workspaceId?: string;
    portalUrl?: string;
    orgId?: string;
    fetchOrgMembers?: boolean;
  }) => {
    return await updatePermissions({
      target: PermissionsResourceType.PORTAL_MENU_ITEM,
      targetId,
      invites,
      workspaceId,
      portalUrl,
      orgId,
      fetchOrgMembers,
      ...permissions,
    }).then((result) => result.data);
  }
);

export const inviteToPortalFx = createEffect(async (props: ManageMembersInvitePayload) => {
  return inviteToPortal(props).then((res) => res.data);
});

export const $manageMembers = createStore<ManageMembersState>(initialManageMembersState)
  .on(updateViewModeEvent, (state, viewMode) => {
    return { ...state, viewMode };
  })
  .on(updateOrgMaxMembersEvent, (state, orgMaxMembers) => {
    return { ...state, orgMaxMembers };
  })
  .on(updateMenuItemEvent, (state, menuItem) => {
    return { ...state, menuItem };
  })
  .on(updateContextEvent, (state, context) => {
    return { ...state, context };
  })
  .on(updateOrgLimitsEvent, (state, orgLimits) => {
    return { ...state, orgLimits };
  })
  .on(updateTypeEvent, (state, value) => {
    return { ...state, type: value };
  })
  .on(resetManageMembersEvent, () => {
    return initialManageMembersState;
  })
  .on(updateDraftInviteCountEvent, (state, draft) => {
    return { ...state, draftInviteCount: draft };
  })
  .on(setIsPaymentInfoActiveEvent, (state, value) => {
    return { ...state, isPaymentInfoActive: value };
  })
  .on(endFetchingEvent, (state) => {
    return { ...state, isFetching: false };
  })
  .on(updateForceIsOnlySharedAvailableEvent, (state, value) => {
    return { ...state, forceIsOnlySharedAvailable: value };
  });

export const $manageMembersType = $manageMembers.map((state) => state.type);
export const $viewMode = $manageMembers.map((state) => state.viewMode);
export const $menuItem = $manageMembers.map((state) => state.menuItem);
export const $context = $manageMembers.map((state) => state.context);
export const $contextWorkspaceId = $context.map((state) => state.workspaceId);
export const $portalConsoleAccess = $accessSettings.map((state) => state.console);
export const $portalClientAccess = $accessSettings.map((state) => state.portal);
export const $draftInviteCount = $manageMembers.map((state) => state.draftInviteCount);
export const $isFetching = $manageMembers.map((state) => state.isFetching);
export const $isPaymentInfoActive = $manageMembers.map((state) => state.isPaymentInfoActive);
export const $forceIsOnlySharedAvailable = $manageMembers.map((state) => state.forceIsOnlySharedAvailable);

export const $workspaceMembers = combine($contextWorkspaceId, $workspaceMembersState).map(
  ([workspaceId, workspaceMemberState]) => {
    return Object.values(workspaceMemberState.entities).filter((member) => member.workspaceId === workspaceId);
  }
);

export const $workspaceMemberUserIds = $workspaceMembers.map((members) => members.map((member) => member.userId));

export const $orgMemberEmails = combine($orgMemberUserIds, $usersState, $invites).map(
  ([orgMemberUserIds, usersState, orgInvites]) => {
    const emails = orgMemberUserIds.reduce((acc, userId) => {
      const user = usersState.entities[userId];
      if (user) {
        acc.push(user.email);
      }
      return acc;
    }, [] as string[]);
    return orgInvites.reduce((acc, invite) => {
      return [...acc, invite.email];
    }, emails);
  }
);

export const $workspaceMemberEmails = combine($workspaceMemberUserIds, $usersState, $workspaceInviteEmails).map(
  ([userIds, usersState, workspaceInviteEmails]) => {
    return userIds.reduce(
      (acc, userId) => {
        const user = usersState.entities[userId];
        if (user) {
          acc.push(user.email);
        }
        return acc;
      },
      [...workspaceInviteEmails]
    );
  }
);

export const $manageMembersWorkspaceMemberUserIds = $workspaceMembers.map((members) =>
  members.map((member) => member.userId)
);
export const $accessMembers = combine($members, $manageMembersWorkspaceMemberUserIds).map(([members, userIds]) => {
  return members.filter((member) => userIds.includes(member.userId));
});
export const $sortedByRoleMembers = $members.map((members) =>
  members.sort((a, b) => memberRolesOrder[a.role] - memberRolesOrder[b.role])
);

export const $showUpgradeMessage = combine(
  $orgRole,
  $billingInfo,
  $orgUsageData,
  $draftInviteCount,
  $hasCoupons,
  $isPaymentInfoActive
).map(([orgRole, billingInfo, orgUsage, draftInviteCount, hasCoupons, isPaymentInfoActive]) => {
  const membersCount = draftInviteCount.member + draftInviteCount.manager + orgUsage.members.current;
  return (
    !hasCoupons &&
    orgRole === OrgMemberRole.OWNER &&
    (!isPaymentInfoActive || !billingInfo.active || billingInfo?.plan === BillingInfoPlan.TRIAL) &&
    orgUsage.members.max < membersCount
  );
});

export const $currentUserCanInvite = combine($orgRole, $workspaceRole).map(([orgRole, workspaceRole]) => {
  return orgRole === 'owner' || orgRole === 'manager' || workspaceRole === 'admin';
});

export const $hasBillingInfo = $billingInfo.map((billingInfo) => billingInfo.active);

export const $clientsDisabled = $orgUsageData.map((orgUsage) => orgUsage.clients.max === 0);

export const getManageMembersDataFx = attach({
  source: $context,
  effect: async (
    context
  ): Promise<{
    orgUsage: OrgUsage;
    workspaceMembers: WorkspaceMember[];
    workspaceInvites: WorkspaceInvite[];
    users: User[];
    orgMembers: OrgMember[];
    orgInvites: OrgInvite[];
    billingInfo: BillingInfo;
    orgInfo: OrgInfo;
    isPaymentInfoActive: boolean;
  }> => {
    return await getManageMembersData({
      orgId: context.orgId,
      workspaceId: context.workspaceId,
    }).then((result) => {
      return result.data;
    });
  },
});

sample({
  source: updatePermissionsFx.doneData,
  fn: (result) => result?.orgMembers ?? [],
  target: upsertMembersEvent,
});

sample({
  source: updatePermissionsFx.doneData,
  fn: (result) => result?.users ?? [],
  target: upsertUsersEvent,
});

sample({
  source: updatePermissionsFx.doneData,
  fn: (result) => result?.workspaceMembers ?? [],
  target: upsertWorkspaceMembersEvent,
});

sample({
  source: updateContextEvent,
  target: [startOrgUsageFetchEvent, getManageMembersDataFx],
});

sample({
  source: getManageMembersDataFx.doneData,
  target: endFetchingEvent,
});

sample({
  source: getManageMembersDataFx.doneData,
  fn: (result) => result?.isPaymentInfoActive ?? false,
  target: setIsPaymentInfoActiveEvent,
});

sample({
  source: getManageMembersDataFx.doneData,
  fn: (result) => result.orgUsage,
  target: endOrgUsageFetchEvent,
});

sample({
  source: getManageMembersDataFx.doneData,
  fn: (result) => result?.orgMembers ?? [],
  target: setMembersEvent,
});

sample({
  source: getManageMembersDataFx.doneData,
  fn: (result) => result?.orgInvites ?? [],
  target: setInvitesEvent,
});

sample({
  source: getManageMembersDataFx.doneData,
  fn: (result) => result?.users ?? [],
  target: setUsersEvent,
});

sample({
  source: getManageMembersDataFx.doneData,
  fn: (result) => result?.workspaceMembers ?? [],
  target: setWorkspaceMembersEvent,
});

sample({
  source: getManageMembersDataFx.doneData,
  fn: (result) => result?.workspaceInvites ?? [],
  target: setWorkspaceInvitesEvent,
});

sample({
  source: getManageMembersDataFx.doneData,
  fn: (result) => result?.billingInfo ?? {},
  target: setBillingInfoEvent,
});

sample({
  source: getManageMembersDataFx.doneData,
  fn: (result) => result?.orgInfo ?? null,
  filter: (result) => !!result?.orgInfo,
  target: setOrgInfoEvent,
});

sample({
  source: inviteToPortalFx.doneData,
  fn: (result) => result?.userIdsForPrivateResources ?? [],
  target: addUserIdsToAllAllowedAllMenuItemsEvent,
});
